import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(33, 26, 18, 0.54);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  padding: var(--space-md);
`;

const ModalContainer = styled.div`
  background: white;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-large);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: var(--space-md);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
`;

const HeaderTitle = styled.h2`
  font-size: var(--font-size-xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  margin: 0;
  color: var(--color-text);
  line-height: 1.2;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: var(--font-size-2xl);
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: var(--space-xs);
  line-height: 1;
  border-radius: var(--border-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-speed);
  
  &:hover {
    color: var(--color-text);
    background-color: var(--color-primary-lighter);
  }
`;

const ModalContent = styled.div`
  padding: var(--space-md);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-md);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--color-border-light);
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: var(--space-sm);
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const Label = styled.div`
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-bold);
  margin-bottom: 2px;
`;

const Value = styled.div`
  font-size: var(--font-size-md);
  color: var(--color-text);
  line-height: 1.5;
`;

const UserInfo = styled(Value)`
  display: flex;
  flex-direction: column;
`;

const UserEmail = styled.span`
  font-weight: var(--font-weight-medium);
`;

const UserId = styled.span`
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-family: monospace;
`;

const MessageSection = styled(Section)`
  flex: 1;
`;

const MessageContainer = styled.div`
  background: var(--color-primary-lighter);
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-border-light);
  min-height: 150px;
`;

const MessageValue = styled.div`
  font-size: var(--font-size-md);
  color: var(--color-text);
  line-height: 1.6;
  white-space: pre-wrap;
`;

const ModalFooter = styled.div`
  padding: var(--space-md);
  border-top: 1px solid var(--color-border);
  background: var(--color-primary-lighter);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-md);
  flex-wrap: wrap;
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: var(--space-md);
    width: 8px;
    height: 8px;
    border-right: 2px solid var(--color-text-secondary);
    border-bottom: 2px solid var(--color-text-secondary);
    transform: translateY(-70%) rotate(45deg);
    pointer-events: none;
    transition: border-color var(--transition-speed) ease;
  }

  &:focus-within::after {
    border-color: var(--color-accent);
  }
`;

const StatusSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: var(--space-xs) var(--space-xl) var(--space-xs) var(--space-md);
  border: 2px solid ${props => {
    switch(props.value) {
      case 'open': return 'rgba(130, 172, 216, 0.36)';
      case 'pending': return 'rgba(180, 83, 9, 0.28)';
      case 'closed': return 'rgba(93, 64, 34, 0.2)';
      default: return 'var(--color-border)';
    }
  }};
  border-radius: var(--border-radius-md);
  background: ${props => {
    switch(props.value) {
      case 'open': return 'rgba(130, 172, 216, 0.12)';
      case 'pending': return 'rgba(180, 83, 9, 0.08)';
      case 'closed': return 'rgba(93, 64, 34, 0.06)';
      default: return 'white';
    }
  }};
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  min-width: 180px;
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  
  &:hover {
    border-color: ${props => {
      switch(props.value) {
        case 'open': return 'var(--color-accent)';
        case 'pending': return 'var(--color-warning-dark)';
        case 'closed': return 'var(--color-text-muted)';
        default: return 'var(--color-primary)';
      }
    }};
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const SaveButton = styled.button`
  background: var(--color-periwinkle);
  color: white;
  border: none;
  padding: var(--space-xs) var(--space-lg);
  border-radius: var(--border-radius-md);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-speed);
  font-size: var(--font-size-md);
  
  &:hover {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: var(--shadow-small);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const TicketDetailModal = ({ ticket, onClose, onUpdateStatus }) => {
  const [status, setStatus] = useState(ticket.status);
  const [updating, setUpdating] = useState(false);

  if (!ticket) return null;

  const handleSave = async () => {
    setUpdating(true);
    await onUpdateStatus(ticket.id, status);
    setUpdating(false);
    onClose();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <HeaderTitle>{ticket.subject}</HeaderTitle>
          <CloseButton onClick={onClose} aria-label="Close modal">&times;</CloseButton>
        </ModalHeader>
        
        <ModalContent>
          <MetaGrid>
            <Section>
              <Label>Submitted By</Label>
              <UserInfo>
                <UserEmail>{ticket.email}</UserEmail>
                <UserId>{ticket.userId}</UserId>
              </UserInfo>
            </Section>
            
            <Section>
              <Label>Date Submitted</Label>
              <Value>{formatDate(ticket.createdAt)}</Value>
            </Section>
          </MetaGrid>

          <MessageSection>
            <Label>Message Content</Label>
            <MessageContainer>
              <MessageValue>{ticket.message}</MessageValue>
            </MessageContainer>
          </MessageSection>
        </ModalContent>

        <ModalFooter>
          <StatusGroup>
             <Label style={{ marginBottom: 0 }}>Status:</Label>
             <SelectWrapper>
               <StatusSelect 
                 value={status} 
                 onChange={(e) => setStatus(e.target.value)}
               >
                 <option value="open">Open</option>
                 <option value="pending">Pending</option>
                 <option value="closed">Closed</option>
               </StatusSelect>
             </SelectWrapper>
          </StatusGroup>
          <SaveButton onClick={handleSave} disabled={updating || status === ticket.status}>
            {updating ? 'Updating...' : 'Update Status'}
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
};

export default TicketDetailModal;
